import type { ReactElement } from 'react';
import { useState } from 'react';

import CookieBanner from '@skyscanner-internal/global-components/cookie-banner';
import { CONFIG_SERVICE_KEYS } from '@web-platform/shared-universal-constants-config';
import { translate } from '@web-platform/shared-universal-translations';
import { getConfigServiceValue } from '@web-platform/shared-universal-utils-frontend/src/config';
import { lazyWithRetry } from '@web-platform/shared-universal-utils-frontend/src/helpers/lazyLoading';
import { getCulture } from '@web-platform/shared-universal-utils-frontend-localisation';
import { GLOBAL } from 'saddlebag-browser';
import { logError, logOperationalEvent, logWarn } from 'saddlebag-logger';

// Lazy-load the Ketch ConsentBanner so its bundle only ships when enableKetchCMP is on.
// The App-level <Suspense fallback={null}> in apps/banana-webapp/src/App.tsx covers
// the loading state — UserPreferencesDialog itself is already lazy-loaded by PageAddons.
const ConsentBanner = lazyWithRetry(
  () =>
    import(
      /* webpackChunkName: "ConsentBanner" */
      '@skyscanner-internal/global-components/unstable__consent-banner'
    ),
);

export type Props = {
  cookieBannerAcknowledgedClick?: () => {};
};

const logger = { logError, logWarn, logOperationalEvent };

const UserPreferencesDialog = ({ cookieBannerAcknowledgedClick }: Props): ReactElement | null => {
  const [isOpen, setIsOpen] = useState(true);
  const { market } = getCulture();
  const enableKetchCMP = !!getConfigServiceValue(CONFIG_SERVICE_KEYS.OPS.ENABLE_KETCH_CMP);
  const cookieBannerFocusElement =
    GLOBAL.getDocument().getElementById('global-header') ||
    GLOBAL.getDocument().getElementById('header-logo-link');

  const handlePolicyAcceptClick = () => {
    !!cookieBannerAcknowledgedClick && cookieBannerAcknowledgedClick();
    setIsOpen(false);

    // When the Cookie Banner OK button is pressed we bring focus back to the first item in the Header, if it exists,
    // if not it ends up on the <body> and needs a Tab press to enter the page.
    cookieBannerFocusElement?.focus();
  };

  const strings = {
    title: translate('cookie_policy_gdpr_title_v6'),
    body: translate('cookie_policy_gdpr_text_v8'),
    explainYourOptions: translate('cookie_policy_explain_your_options'),
    manageOptions: translate('cookie_policy_manage_options_label_v3'),
    close: translate('close_label'),
    consentInformationSectionA: translate('cookie_policy_section_a'),
    consentInformationSectionB: translate('cookie_policy_section_b'),
    consentInformationSectionD: translate('cookie_policy_section_d'),
    declineAllSectionA: translate('cookie_policy_decline_all_section_a'),
    declineAllSectionB: translate('cookie_policy_decline_all_section_b'),
    declineAllSectionC: translate('cookie_policy_decline_all_section_c'),
    declineAllButtonAcceptAll: translate('cookie_policy_decline_all_button_accept_all'),
    declineAllButtonManageSettings: translate('cookie_policy_decline_all_button_manage_settings'),
    declineAllButtonAcceptEssentialOnly: translate(
      'cookie_policy_decline_all_button_accept_essential_only',
    ),
  };

  if (!isOpen) {
    return null;
  }

  return enableKetchCMP ? (
    <ConsentBanner
      isProductionEnv={process.env.NODE_ENV === 'production'}
      onPolicyAcceptClick={handlePolicyAcceptClick}
      strings={strings}
      logger={logger}
    />
  ) : (
    <CookieBanner
      isProductionEnv={process.env.NODE_ENV === 'production'}
      market={market}
      onPolicyAcceptClick={handlePolicyAcceptClick}
      strings={strings}
      totalNumberOfVendors={getConfigServiceValue(CONFIG_SERVICE_KEYS.OPS.TOTAL_NUMBER_OF_VENDORS)}
      transparencyConsentFrameworkConfigVersionNumber={getConfigServiceValue(
        CONFIG_SERVICE_KEYS.OPS.TRANSPARENCY_CONSENT_FRAMEWORK_CONFIG_VERSION,
      )}
      logger={logger}
    />
  );
};

// Required for dynamic import used by Webpack
export default UserPreferencesDialog;
